import React, { useEffect, useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { maxBy } from 'lodash';

import { useTranslation } from 'react-i18next';

// moment
import moment from 'moment';
import DateFnsUtils from '@date-io/moment';

// HighCharts
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExport from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';

// material-ui
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import Input from '@material-ui/core/Input';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import UserContext from '../../../context/UserContext';
// init highcharts module
HighchartsExport(Highcharts);
HighchartsExportData(Highcharts);

export default function WaterQualityChart(props) {
  const { t } = useTranslation();
  const { id } = props;
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
  const today = moment(new Date()).locale('th').format('YYYY-MM-DD');
  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [dataParam, setDataParam] = useState([]);
  const [dataStation, setDataStation] = useState([]);
  const [paramSelect, setParamSelect] = useState('salinity');
  const [startDate, setStartDate] = useState(moment().subtract(3, 'days').locale('th').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment(new Date()).locale('th').format('YYYY-MM-DD'));
  const [chartOption, setChartOptions] = useState();
  const [stationSelect, setStationSelect] = useState([id]);
  const [dataChart, setDataChart] = useState(undefined);
  const { userIsLogin } = useContext(UserContext);

  function getChartOptions() {
    const options = {
      title: {
        text: '',
      },
      plotOptions: {
        line: {
          turboThreshold: 0,
        },
      },
      xAxis: {
        type: 'datetime',
        labels: {
          format: '{value:%d-%b<br />%H:%M}',
        },
      },
      yAxis: {
        // Primary yAxis
        tickInterval: 0.2,
        title: {
          text: `${t('ความเค็ม')} (g/L)`,
        },
        labels: {
          format: '{value:.2f}',
        },
        minorGridLineWidth: 1,
        gridLineWidth: 1,
        alternateGridColor: null,
        plotLines: [
          {
            value: null,
            color: 'rgba(255, 139, 4, 1)',
            dashStyle: 'shortdash',
            width: 1,
            label: {
              text: null,
              align: 'left',
            },
          },
          {
            value: null,
            color: 'red',
            dashStyle: 'shortdash',
            width: 1,
            label: {
              text: null,
              align: 'left',
            },
          },
          {
            value: null,
            color: '#4eac5b', // เขียว
            dashStyle: 'shortdash',
            width: 2,
            label: {
              text: null,
              align: 'right',
            },
          },
          {
            value: null,
            color: '#f6c143', // เหลือง
            dashStyle: 'shortdash',
            width: 2,
            label: {
              text: null,
              align: 'right',
            },
          },
          {
            value: null,
            color: '#ea3323', // แดง
            dashStyle: 'shortdash',
            width: 2,
            label: {
              text: null,
              align: 'left',
            },
          },
          {
            value: null,
            color: '#b02318', // เลือดหมู
            dashStyle: 'shortdash',
            width: 2,
            label: {
              text: null,
              align: 'right',
            },
          },
          {
            value: null,
            color: '#ea3323', // แดง
            dashStyle: 'shortdash',
            width: 2,
            label: {
              text: null,
              align: 'right',
            },
          },
        ],
        plotBands: [],
      },
      legend: {
        enabled: true,
      },
      tooltip: {
        shared: true,
      },
      series: [
        {
          name: t('ความเค็ม'),
          data: [],
          tooltip: {
            valueSuffix: `${t('ความเค็ม')} (g/L)`,
          },
        },
      ],
      exporting: {
        buttons: {
          contextButton: {
						enabled: userIsLogin,
            menuItems: [
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
    };
    return options;
  }

  function findMaxValue(data) {
    let arrayMaxValue = [];
    data.forEach((element) => {
      const dataCleanValid = element.data.filter(x => x.value !== null);
      const max = maxBy(dataCleanValid, 'value').value;
      arrayMaxValue.push(max);
    });
    const obj = {
      max: Math.max(...arrayMaxValue),
      min: Math.min(...arrayMaxValue)
    }
    return obj;
  }

  const renderChart = useCallback(async () => {
    await fetch(`${process.env.REACT_APP_API_URL}/analyst/waterquality_compare_station_graph?waterquality_station_id=${stationSelect}&param=${paramSelect}&start_datetime=${startDate} 00:00&end_datetime=${endDate} 23:59`)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        return 'Something went wrong.';
      })
      .then((result) => {
        if (result.data[0]) {
          const dataClean = result.data[0].data.filter(x => x.value !== null);
          if(dataClean.length > 0) {
            setDataChart(result.data[0]);
            // transform datas
            const dataSeries = [];
            let ts = null;
            let val = null;
            // const max = maxBy(dataClean, 'value').value;
            // const min = minBy(dataClean, 'value').value;
            const findMaxMinData = findMaxValue(result.data);
            const max = findMaxMinData.max;
            const min = findMaxMinData.min;
            result.data.map((data) => {
              const series = [];
              data.data.map((n) => {
                val = parseFloat(n.value);
                ts = moment(n.datetime, 'YYYY/MM/DD hh:mm:ss')
                  .add(7, 'hours')
                  .valueOf();
                if (val === max || val === min) {
                  const marker = {
                    enabled: false,
                    symbol: '',
                    fillColor: '#06C',
                    radius: 5,
                  };
                  series.push({ x: ts, y: val, marker });
                } else if (!Number.isNaN(val)) {
                  series.push([ts, val]);
                }
                return series;
              });
              dataSeries.push({
                name: `${data.series_name.th}`,
                data: series,
                tooltip: {
                  valueSuffix: ' ',
                  xDateFormat: '%d %B %Y %H:%M',
                },
              });
              return dataSeries;
            });
            const chartOptions = getChartOptions();
            chartOptions.yAxis.min = 0;
            chartOptions.series = dataSeries;
            dataParam.map((row) => {
              if (row.value === paramSelect) {
                chartOptions.yAxis.title.text = row.name.th;
              }
              return chartOptions;
            });
            // for another parameter
            chartOptions.yAxis.max = 1 + max;
            // ความเค็ม
            if (paramSelect === 'salinity') {
              // plotLines
              if (max < 1.9) {
                chartOptions.yAxis.max = 2.01;
              }
              chartOptions.yAxis.plotLines[5].value = 2;
              chartOptions.yAxis.plotLines[5].label.text = `<strong>${t('มากกว่า 2 เกินเกณฑ์มาตรฐานน้ำใช้เพื่อการเกษตร')}</strong>`;
              chartOptions.yAxis.plotLines[4].value = 0.5;
              chartOptions.yAxis.plotLines[4].label.text = `<strong>${t('มากกว่า 0.5 เกินเกณฑ์คุณภาพน้ำเพื่อการผลิตน้ำประปา')}</strong>`;
              chartOptions.yAxis.plotLines[3].value = 0.25;
              chartOptions.yAxis.plotLines[3].label.text = `<strong>${t('มากกว่า 0.25 เกณฑ์เฝ้าระวังคุณภาพน้ำเพื่อการผลิตน้ำประปา')}</strong>`;
            }
            // ออกซิเจนในน้ำ
            if (paramSelect === 'do') {
              if (Math.floor(max) === 0) {
                chartOptions.yAxis.max = 2.01;
              } else {
                chartOptions.yAxis.max = 1.5 + max;
              }
              chartOptions.yAxis.plotLines[4].value = 2;
              chartOptions.yAxis.plotLines[4].label.text = `<strong>${t('น้อยกว่า 2 เกินเกณฑ์คุณภาพน้ำเพื่อการผลิตน้ำประปา')}</strong>`;
            }
            // กรด-ด่าง
            if (paramSelect === 'ph') {
              if (max < 5) {
                chartOptions.yAxis.max = 5;
              } else {
                chartOptions.yAxis.max = 1 + max;
              }
              chartOptions.yAxis.plotLines[4].value = 5;
              chartOptions.yAxis.plotLines[4].label.text = `<strong>${t('น้อยกว่า 5 เกินเกณฑ์คุณภาพน้ำเพื่อการผลิตน้ำประปา')}</strong>`;
              chartOptions.yAxis.plotLines[6].value = 9;
              chartOptions.yAxis.plotLines[6].label.text = `<strong>${t('มากกว่า 9 เกินเกณฑ์คุณภาพน้ำเพื่อการผลิตน้ำประปา')}</strong>`;
            }
            // ความขุ่น
            if (paramSelect === 'turbid') {
              if (Math.floor(max) === 0) {
                chartOptions.yAxis.max = 1200;
              } else if (Math.floor(max) < 100) {
                chartOptions.yAxis.max = 100 + Math.floor(max);
              } else if (Math.floor(max) >= 1000) {
                chartOptions.yAxis.max = 200 + Math.floor(max);
              }
              chartOptions.yAxis.plotLines[4].value = 1000;
              chartOptions.yAxis.plotLines[4].label.text = `<strong>${t('มากกว่า 1,000 เกินเกณฑ์คุณภาพน้ำเพื่อการผลิตน้ำประปา')}</strong>`;
              chartOptions.yAxis.plotLines[3].value = 100;
              chartOptions.yAxis.plotLines[3].label.text = `<strong>${t('มากกว่า 100 เกณฑ์เฝ้าระวังคุณภาพน้ำเพื่อการผลิตน้ำประปา')}</strong>`;
            }
            // สารละลาย
            if (paramSelect === 'tds') {
              if (Math.floor(max) === 0) {
                chartOptions.yAxis.max = 2001;
              } else if (Math.floor(max) < 1001) {
                chartOptions.yAxis.max = 1001;
              } else if (Math.floor(max) >= 2001) {
                chartOptions.yAxis.max = 200 + Math.floor(max);
              }
              chartOptions.yAxis.plotLines[4].value = 2001;
              chartOptions.yAxis.plotLines[4].label.text = `<strong>${t('มากกว่า 2,001 เกินเกณฑ์คุณภาพน้ำเพื่อการผลิตน้ำประปา')}</strong>`;
              chartOptions.yAxis.plotLines[3].value = 1001;
              chartOptions.yAxis.plotLines[3].label.text = `<strong>${t('มากกว่า 1,001 เกณฑ์เฝ้าระวังคุณภาพน้ำเพื่อการผลิตน้ำประปา')}</strong>`;
            }
            setChartOptions(chartOptions);
          } else {
            setDataChart(undefined);
          }
        } else {
          setDataChart(undefined);
          const chartOptions = getChartOptions();
          setChartOptions(chartOptions);
        }
      })
      .catch((err) => setError(err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataParam, endDate, paramSelect, startDate, stationSelect]);

  const handleParamChange = (event) => {
    setParamSelect(event.target.value);
    renderChart();
  };

  const handleChange = (event) => {
    if (event.target.name === 'stationSelect') {
      if (event.target.value.length <= 5) {
        setStationSelect(event.target.value);
      }
    } else {
      setStationSelect(event.target.value);
    }
  };

  const multiSelectStationRenderer = (selected) => {
    const stationName = [];
    selected.map((n) => {
      const dd = dataStation.find((st) => st.id === n);
      if (typeof dd !== 'undefined') {
        stationName.push(dd.waterquality_station_name.th);
      }
      return stationName;
    });
    return <span>{stationName.join(', ')}</span>;
  };

  const handleStartDateChange = (date) => {
    setStartDate(moment(date).format('YYYY-MM-DD'));
    renderChart();
  };

  const handleEndDateChange = (date) => {
    setEndDate(moment(date).format('YYYY-MM-DD'));
    renderChart();
  };

  const handleChart = () => {
    renderChart();
  };

  useEffect(() => {
    const fetchData = async () => {
      fetch(`${process.env.REACT_APP_API_URL}/analyst/waterquality_load`)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Something went wrong ...');
        })
        .then((result) => {
          setDataStation(result.waterquality_station.data);
          const rawDataParam = result.waterquality_param.data;
          const dataParams = rawDataParam.filter((e) => e.value !== 'conductivity' && e.value !== 'chlorophyll' && e.value !== 'temp');
          setDataParam(dataParams);
        })
        .catch((err) => setError(err));
      renderChart();
      setLoading(false);
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, id, startDate]);

  return (
    <>
      <Grid>
        {error && <div>Error Occured</div>}
        {isLoading && (
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} size="3x" />
        </div>
        )}
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm>
            <FormControl>
              <InputLabel shrink htmlFor="province-label-placeholder">
                {t('พารามิเตอร์')}
              </InputLabel>
              <Select value={paramSelect} onChange={handleParamChange} id="dropdown_parameter">
                {dataParam.map((row) => (
                  <MenuItem key={row.value} value={row.value}>
                    {t(row.name.th)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm>
            <FormControl>
              <InputLabel shrink htmlFor="station">
                {t('สถานีคุณภาพน้ำ')}
              </InputLabel>
              <Select
                id="dropdown_station"
                multiple
                value={stationSelect}
                onChange={handleChange}
                input={<Input id="station" />}
                renderValue={multiSelectStationRenderer}
                MenuProps={MenuProps}
                inputProps={{
                  name: 'stationSelect',
                  id: 'station',
                }}
              >
                {dataStation.map((row) => (
                  <MenuItem key={row.id} value={row.id}>
                    <Checkbox checked={stationSelect.indexOf(row.id) > -1} />
                    <ListItemText primary={row.waterquality_station_name.th} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm>
            <FormControl>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  id="dropdown_startdate"
                  label={t('วันที่เริ่มต้น')}
                  format="YYYY-MM-DD"
                  value={startDate}
                  maxDate={today}
                  onChange={handleStartDateChange}
                />
              </MuiPickersUtilsProvider>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm>
            <FormControl>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  id="dropdown_enddate"
                  label={t('วันที่สิ้นสุด')}
                  format="YYYY-MM-DD"
                  value={endDate}
                  maxDate={today}
                  minDate={startDate}
                  onChange={handleEndDateChange}
                />
              </MuiPickersUtilsProvider>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm>
            <FormControl>
              <Button
                id="btn_showchart"
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleChart}
                title={t('แสดงกราฟ')}
              >
                แสดง
              </Button>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            {
              dataChart ? (
                <HighchartsReact highcharts={Highcharts} options={chartOption} />
              ) : (
                <div className="text-center">{t('ไม่มีข้อมูลในช่วงเวลาที่เลือก')}</div>
              )
            }
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

WaterQualityChart.propTypes = {
  id: PropTypes.number.isRequired,
  provinceId: PropTypes.string.isRequired,
};
